import React, {useMemo, useEffect, useRef} from 'react';
import Select, { components, SingleValueProps, OptionProps } from 'react-select';
import { selectStyles, selectTheme } from '@/styles/selectTheme';
import { loadFont, preloadFonts } from '@/services/googleFontsService';

interface FontPickerProps {
    value: string;
    onChange: (fontFamily: string) => void;
    availableFonts?: string[];
    disabled?: boolean;
    height?: PickerHeight;
}

interface FontOption {
    value: string;
    label: string;
}

type PickerHeight = '32px' | '40px';

const FontPicker: React.FC<FontPickerProps> = ({ value, onChange, availableFonts, disabled, height = '32px' }) => {
    const fonts = availableFonts || [];

    useEffect(() => {
        const topFonts = fonts.slice(0, 20);
        preloadFonts(topFonts);
    }, [fonts]);

    const options: FontOption[] = useMemo(() => {
        return fonts.map(fontFamily => ({
            value: fontFamily,
            label: fontFamily.replace(/Noto (Sans|Serif).+/, 'Noto $1'),
        }));
    }, [fonts]);

    const currentValue = useMemo(() => {
        return options.find(option => option.value === value) || { value, label: value };
    }, [options, value]);

    const handleChange = (newValue: unknown) => {
        const option = newValue as FontOption | null;
        if (option) {
            loadFont(option.value, true).catch(err => {
                console.error('Failed to load font:', err);
            });
            onChange(option.value);
        }
    };

    const SingleValue: React.FC<SingleValueProps<FontOption>> = (props) => (
        <components.SingleValue {...props}>
            <div className="notranslate" style={{ fontFamily: props.data.value }}>{props.data.label}</div>
        </components.SingleValue>
    );

    const Option: React.FC<OptionProps<FontOption>> = (props) => {
        const ref = useRef<HTMLDivElement | null>(null) as React.MutableRefObject<HTMLDivElement | null>;

        useEffect(() => {
            loadFont(props.data.value, false).catch(() => {});
        }, [props.data.value]);

        useEffect(() => {
            props.isSelected && ref.current?.scrollIntoView();
        }, [props.isSelected]);

        return (
            <components.Option {...props} innerRef={(instance: HTMLDivElement | null) => {
                ref.current = instance;
            }}>
                <div className="notranslate" style={{ fontFamily: props.data.value }}>{props.data.label}</div>
            </components.Option>
        );
    };

    return (
        <div className="relative">
            <Select
                value={currentValue}
                onChange={handleChange}
                options={options}
                isDisabled={disabled}
                isSearchable
                placeholder="Select font..."
                className="text-sm"
                menuPortalTarget={document ? document.body : undefined}
                styles={{
                    ...selectStyles,
                    control: (base, state) => ({
                        ...selectStyles.control?.(base, state),
                        minHeight: height,
                        fontSize: '0.875rem',
                    }),
                    valueContainer: (base) => ({
                        ...base,
                        padding: '0 8px',
                    }),
                    dropdownIndicator: (base, state) => ({
                        ...selectStyles.dropdownIndicator?.(base, state),
                        padding: '4px',
                    }),
                    indicatorSeparator: () => ({
                        display: 'none',
                    }),
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                    })
                }}
                theme={selectTheme}
                components={{
                    SingleValue,
                    Option,
                }}
            />
        </div>
    );
};

export default FontPicker;
